import React, { useState, useEffect } from 'react'
import { SignIn } from '../../components/Login/SignIn'
import {
	getAuth,
	RecaptchaVerifier,
	getMultiFactorResolver,
	PhoneMultiFactorGenerator,
	PhoneAuthProvider,
	sendPasswordResetEmail
} from 'firebase/auth'

import {
	Container,
	CssBaseline,
	makeStyles,
	Card,
	CardContent,
	Toolbar,
	Typography,
	AppBar
} from '@material-ui/core'

import { useHistory } from 'react-router-dom'
import logo from '../../assets/svg/skiptown.svg'

import { showError, showSuccess } from '../../actions/misc/misc.actions'
import { useDispatch, useSelector } from 'react-redux'
import Alert from '../../components/Misc/Alert'

import { isEmailValid } from '../../services/helpers'

const useStyles = makeStyles((theme) => ({
	container: {
		background: '#E5E5E5',
		minHeight: '100vh',
		paddingTop: 144,
		paddingBottom: 16,
		textAlign: 'center',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between'
	},
	appBar: {
		border: 'none'
	},
	toolbar: {
		background: theme.palette.primary.main
	},
	title: {
		fontWeight: 800,
		fontSize: '16px',
		lineHeight: '22px',
		color: '#FFFFFF',
		textTransform: 'uppercase'
	},
	card: {
		maxWidth: 500,
		margin: '0 auto',
		padding: '48px 86px 64px 86px'
	},
	logo: {
		height: 34,
		marginRight: 16
	}
}))

const Login = (props) => {
	// console.log('props....', props)

	const classes = useStyles()
	const history = useHistory()
	const dispatch = useDispatch()

	const alert = useSelector((state) => state.general.main.alert)

	// const [isCreateAccountShown, setIsCreateAccountShown] = useState(false)

	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [factorCode, setFactorCode] = useState('')
	const [emailError, setEmailError] = useState(false)
	const [invalidEmailError, setInvalidEmailError] = useState(false)
	const [passwordError, setPasswordError] = useState(false)
	const [wrongPasswordTries, setWrongPasswordTries] = useState(0)
	const [factorQuestion, showFactorQuestion] = useState(false)
	const [verificationId, setVerificationId] = useState('')
	const [mfaError, setMfaError] = useState('')
	const [redirect, setRedirect] = useState('')
	const [submittingMFA, setSubmittingMFA] = useState(false)

	const auth = getAuth()

	useEffect(() => {
		if (factorCode > '' && factorCode.length === 6) {
			submitFactor()
			setSubmittingMFA(true)
		} else {
			setSubmittingMFA(false)
		}
	}, [factorCode, setSubmittingMFA])

	useEffect(() => {
		if (props?.location?.state?.from?.pathname) {
			setRedirect(props.location.state.from.pathname)
		}
	}, [props])

	useEffect(() => {
		window.recaptchaVerifier = null
	}, [])

	useEffect(() => {
		if (mfaError) setMfaError('')
	}, [factorCode])

	const updateEmail = (value) => {
		if (emailError) setEmailError(false)

		setInvalidEmailError(value && !isEmailValid(value) ? true : false)

		setEmail(value)
	}

	const updatePassword = (value) => {
		if (passwordError) setPasswordError(false)

		setPassword(value)
	}

	const submitFactor = () => {
		try {
			const cred = PhoneAuthProvider.credential(verificationId, factorCode)
			const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)

			window.mfaResolver
				.resolveSignIn(multiFactorAssertion)
				.then(function (userCredential) {
					// avoids race condition with the main app redirect in App.js
					// console.log('REDIRECDT HERE....', redirect)
					if (redirect && redirect !== '/')
						setTimeout(() => {
							history.push(redirect)
						}, 500)
				})
				.catch((e) => {
					if (e?.code === 'auth/invalid-verification-code') {
						setMfaError('Incorrect MFA Code')
					} else {
						setMfaError('Error signing in')
					}
				})
		} catch (e) {
			if (e?.code === 'auth/invalid-verification-code') {
				setMfaError('Incorrect MFA Code')
			} else {
				setMfaError('Error signing in')
			}
		}
	}

	const handleLogin = async (e) => {
		if ((e && e.key === 'Enter') || e.type === 'submit') {
			e.stopPropagation()

			if (factorQuestion && factorCode.length && !submittingMFA) {
				submitFactor()
				return
			}

			let isValid = isLoginValid()

			// console.log('is valid?', isValid)

			if (!isValid) {
				return
			}

			try {
				await props.firebase.loginWithEmail(email, password)
			} catch (e) {
				// console.log('err...', e)
				if (e.code == 'auth/multi-factor-auth-required') {
					const resolver = getMultiFactorResolver(auth, e)
					window.mfaResolver = resolver
					if (resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
						if (!window.recaptchaVerifier) {
							const captchaNode = document.getElementById('captcha-container')
							if (captchaNode) {
								captchaNode.innerHTML = `<div id='captcha'></div>`
							}
							window.recaptchaVerifier = new RecaptchaVerifier(auth, 'captcha', {
								size: 'invisible',
								callback: async (response) => {
									// console.log('response...', response)
								}
							})
						}

						const phoneInfoOptions = {
							multiFactorUid: resolver.hints[0].uid,
							session: resolver.session
						}

						var phoneAuthProvider = new PhoneAuthProvider(auth)
						// Send SMS verification code.
						return phoneAuthProvider
							.verifyPhoneNumber(phoneInfoOptions, window.recaptchaVerifier)
							.then(function (ourId) {
								// verificationId will be needed for sign-in completion.
								setVerificationId(ourId)
								showFactorQuestion(true)
							})
					} else {
						// Unsupported second factor.
						// Note that only phone second factors are currently supported.
						dispatch(showError('Wrong MFA Type'))
						return
					}
				} else {
					// Handle other errors such as wrong password.
					console.log('Errrr........', e)
					let message
					let code = e.code
					let tries = wrongPasswordTries

					if (code && code === 'auth/wrong-password') {
						message = 'The password supplied is incorrect, please try again.'
						setWrongPasswordTries(tries++)
					}

					if (code && code === 'auth/too-many-requests') {
						message = e.message
						setWrongPasswordTries(tries++)
					}

					dispatch(showError(message || 'Error signing in'))
					return
				}
			}
		}
	}

	const isLoginValid = () => {
		let emailValid = isEmailValid(email)

		if (!email || !emailValid) {
			setEmailError(true)
		}

		if (!password) {
			setPasswordError(true)
		}

		return Boolean(email) && Boolean(emailValid) && Boolean(password)
	}

	const resetPassword = async () => {
		try {
			await sendPasswordResetEmail(auth, email)
			dispatch(showSuccess('Password reset email sent'))
		} catch (e) {
			console.log('reset er', e)
			dispatch(showError('Error resetting password'))
		}
	}

	return (
		<Container className={classes.container} component="main" maxWidth={false}>
			<CssBaseline />
			<AppBar position="fixed" className={classes.appBar} color="primary">
				<Toolbar className={classes.toolbar}>
					<img src={logo} alt="Logo" className={classes.logo} />
					<Typography className={classes.title}>Facility Admin</Typography>
				</Toolbar>
			</AppBar>
			<Card className={classes.card}>
				<CardContent style={{ padding: 0 }}>
					<SignIn
						email={email}
						setEmail={updateEmail}
						password={password}
						emailError={emailError}
						invalidEmailError={invalidEmailError}
						passwordError={passwordError}
						setPassword={updatePassword}
						handleLogin={handleLogin}
						factorQuestion={factorQuestion}
						factorCode={factorCode}
						setFactorCode={setFactorCode}
						submitFactor={submitFactor}
						resetPassword={resetPassword}
						mfaError={mfaError}
						submittingMFA={submittingMFA}
					/>
					<Alert {...alert} />
				</CardContent>
			</Card>
			<Typography
				variant="body1"
				style={{
					fontWeight: 600,
					fontSize: '16px',
					lineHeight: '22px'
				}}>
				© 2025 Skipper, Inc. All rights reserved.
			</Typography>
		</Container>
	)
}

export default Login
